export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  AGENT = 'AGENT',
  CLIENT_ADMIN = 'CLIENT_ADMIN',
  END_USER = 'END_USER'
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED'
}

export enum TicketStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL', // Waiting for CLIENT_ADMIN approval
  REJECTED = 'REJECTED', // Rejected by CLIENT_ADMIN
  NEW = 'NEW', // Approved, waiting for assignment
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING = 'PENDING',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED'
}

// TicketPriority enum removed - priority is not used anymore

export enum TicketCategory {
  TECHNICAL_SUPPORT = 'TECHNICAL_SUPPORT',
  BILLING = 'BILLING',
  FEATURE_REQUEST = 'FEATURE_REQUEST',
  BUG_REPORT = 'BUG_REPORT',
  GENERAL_INQUIRY = 'GENERAL_INQUIRY',
  INSTALLATION = 'INSTALLATION',
  CONFIGURATION = 'CONFIGURATION',
  OTHER = 'OTHER'
}

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
}

export interface Company {
  _id: string;
  name: string;
  type: 'SERVICE_PROVIDER' | 'CLIENT';
  email: string;
  phone?: string;
  address?: Address;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  logo?: string;
  icon?: string;
  branding?: {
    primaryColor?: string;
    sidebarColor?: string;
    sidebarTextColor?: string;
    buttonTextColor?: string;
  };
  billing?: CompanyBilling;
  isOwner?: boolean;
  modules?: {
    projects?: boolean;
    projectsHidden?: boolean;
    projectsTrialStartedAt?: string;
    projectsTrialEndsAt?: string;
  };
}

export interface CompanyBilling {
  issuer?: string;
  contactName?: string;
  hourlyRate?: number;
  currency?: string;
  taxId?: string;
  swiftCode?: string;
  iban?: string;
  cardNumber?: string;
  bankName?: string;
  correspondentBank1?: string;
  correspondentBank2?: string;
  correspondentBank3?: string;
  correspondentBank4?: string;
  correspondentBank5?: string;
  correspondentCountry1?: string;
  correspondentCountry2?: string;
  correspondentCountry3?: string;
  correspondentCountry4?: string;
  correspondentCountry5?: string;
  correspondentSwift1?: string;
  correspondentSwift2?: string;
  correspondentSwift3?: string;
  correspondentSwift4?: string;
  correspondentSwift5?: string;
  paymentInstructions?: string;
}

export interface InvoiceItem {
  code?: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface Invoice {
  _id: string;
  invoiceNumber?: number;
  invoiceNumberStr?: string;
  issuerName: string;
  issuerCompany?: string;
  issuerAddress?: string;
  issuerCity?: string;
  issuerState?: string;
  issuerCountry?: string;
  issuerZipCode?: string;
  issuerPhone?: string;
  issuerEmail?: string;
  issuerLogoUrl?: string;
  issuerSignatureUrl?: string;
  issuerSwiftCode?: string;
  issuerIban?: string;
  issuerCardNumber?: string;
  issuerBankName?: string;
  issuerCorrespondentBank1?: string;
  issuerCorrespondentBank2?: string;
  issuerCorrespondentBank3?: string;
  issuerCorrespondentBank4?: string;
  issuerCorrespondentBank5?: string;
  issuerCorrespondentCountry1?: string;
  issuerCorrespondentCountry2?: string;
  issuerCorrespondentCountry3?: string;
  issuerCorrespondentCountry4?: string;
  issuerCorrespondentCountry5?: string;
  issuerCorrespondentSwift1?: string;
  issuerCorrespondentSwift2?: string;
  issuerCorrespondentSwift3?: string;
  issuerCorrespondentSwift4?: string;
  issuerCorrespondentSwift5?: string;
  issuerPaymentInstructions?: string;
  clientCompany?: string;
  clientName: string;
  clientEmail: string;
  clientCompanyName?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientCountry?: string;
  clientZipCode?: string;
  clientPhone?: string;
  clientTaxId?: string;
  items: InvoiceItem[];
  amount: number;
  currency?: string;
  notes?: string;
  description?: string;
  dueDate: string;
  isPaid?: boolean;
  paidAt?: string;
  paymentMethod?: string;
  reminderEnabled?: boolean;
  source?: 'MANUAL' | 'HOURS';
  createdAt: string;
}

export interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  username?: string;
  role: UserRole;
  company: Company;
  status: UserStatus;
  timezone: string;
  timezoneAuto?: boolean;
  language: string;
  phone?: string;
  avatar?: string;
  lastLogin?: string;
  twoFactorEnabled?: boolean;
  twoFactorRecoveryCodesRemaining?: number;
  notifyEmail?: boolean;
  notifyBrowser?: boolean;
  notifySound?: boolean;
  canViewInvoices?: boolean;
  canViewDeals?: boolean;
}

export interface StatusHistory {
  status: TicketStatus;
  changedBy: User;
  changedAt: string;
  note?: string;
}

export interface Ticket {
  _id: string;
  ticketNumber: string;
  title: string;
  description: string;
  category: TicketCategory;
  status: TicketStatus;
  clientCompany?: Company | null;
  createdBy?: User | null;
  assignedTo?: User;
  dueDate?: string;
  resolvedAt?: string;
  closedAt?: string;
  statusHistory?: StatusHistory[];
  tags?: string[];
  attachments?: string[];
  createdAt: string;
  updatedAt: string;
  supportUsage?: {
    dealId: string;
    dealName: string;
    includedHours: number;
    usedHours: number;
    coveredHours: number;
    overageHours: number;
    remainingHours: number;
    overageRate: number;
    periodLabel: string;
  } | null;
}

export interface ChatMessage {
  _id: string;
  ticket: string;
  sender: User;
  message: string;
  messageType: 'TEXT' | 'FILE' | 'SYSTEM';
  attachments?: string[];
  isRead: boolean;
  createdAt: string;
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  refreshToken?: string;
  user?: User;
  requiresTwoFactor?: boolean;
  twoFactorTempToken?: string;
  message?: string;
} 